import React, { useState, useEffect, useRef } from 'react';
import { IoClose, IoArrowBack } from 'react-icons/io5';
import { BsInfoCircle } from 'react-icons/bs';
import LabSlotModal from './LabSlotModal';
import { PALETTES } from '../utils/colorUtils';
import useConfig from '../config';
import { searchFaculty } from '../utils/facultySearch';
import { Faculty } from '../utils/types';
import { determineIfCourseHasLab } from '../utils/courseUtils';

interface FacultyPreferenceModalProps {
  isOpen: boolean;
  onClose: (currentFacultyPreferences?: string[], currentLabAssignments?: Map<string, string[]>) => void;
  onForceClose: () => void;
  onSubmit: (facultyPreferences: string[], includeLabCourse?: boolean, facultyLabAssignments?: Map<string, string[]>) => void;
  courseName: string;
  courseCredits: number;
  initialFacultyPreferences?: string[];
  initialFacultyLabAssignments?: Map<string, string[]>;
  allCurrentlyUsedSlots: string[];
  slotConflictPairs: string[][];
  isLabCourseAssociated?: boolean;
  palette: keyof typeof PALETTES;
  colorIndex: number;
  hasLabComponent?: boolean;
  isSlashCommandSelection?: boolean;
  allFacultyData: Faculty[];
}

const isMorningLab = (slot: string): boolean => {
    const num = parseInt(slot.slice(1));
    return !isNaN(num) && num >= 1 && num <= 30;
};

const isEveningLab = (slot: string): boolean => {
    const num = parseInt(slot.slice(1));
    return !isNaN(num) && num >= 31 && num <= 60;
};

// Define column IDs
const LEFT_COLUMN = 'left-column';
const RIGHT_COLUMN = 'right-column';

const FacultyPreferenceModal: React.FC<FacultyPreferenceModalProps> = ({
  isOpen,
  onClose,
  onForceClose,
  onSubmit,
  courseName,
  courseCredits,
  initialFacultyPreferences = [],
  initialFacultyLabAssignments,
  allCurrentlyUsedSlots,
  slotConflictPairs,
  isLabCourseAssociated,
  palette,
  colorIndex,
  hasLabComponent,
  isSlashCommandSelection,
  allFacultyData
}) => {
  const config = useConfig();

  // Extract slot name from courseName for display
  const slotDisplay = courseName.includes(' ') ? courseName.split(' ').pop() : '';

  // Determine the base string for course name display (courseName without the slot part)
  let baseNameToFilter = courseName;
  if (slotDisplay) {
    const parts = courseName.split(' ');
    if (parts.length > 1 && parts[parts.length - 1] === slotDisplay) {
      baseNameToFilter = parts.slice(0, -1).join(' ');
    }
  }

  const courseNameDisplay = baseNameToFilter
    .split(' ')
    .filter(part => part.trim() !== '' && !['Edit', 'Faculty', 'Lab', 'Add'].includes(part))
    .join(' ');

  const courseInfo = allFacultyData.find(f => f.TITLE.toLowerCase() === courseNameDisplay.toLowerCase());
  const courseType = courseInfo?.TYPE;
  const courseCode = courseInfo?.CODE;

  const theoryCourseSlots = slotDisplay ? slotDisplay.split('+').filter(Boolean) : [];

  const [facultyName, setFacultyName] = useState('');
  const [facultySearchResults, setFacultySearchResults] = useState<Array<{ name: string; labSlots: string[]; id: string; hasConflict: boolean }>>([]);
  const [isFacultySearchVisible, setIsFacultySearchVisible] = useState(false);
  // Split faculty preferences into left and right columns
  const [leftColumnFaculty, setLeftColumnFaculty] = useState<string[]>([]);
  const [rightColumnFaculty, setRightColumnFaculty] = useState<string[]>([]);
  const [showInput, setShowInput] = useState(true);
  const [isLabModalOpen, setIsLabModalOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [confirmedLabAssignments, setConfirmedLabAssignments] = useState<Map<string, string[]> | undefined>(undefined);
  const [initialFacultyState, setInitialFacultyState] = useState<string[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showEditLab, setShowEditLab] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [duplicateFaculty, setDuplicateFaculty] = useState<string | null>(null);
  const [showManualAddHint, setShowManualAddHint] = useState(false);
  const [focusedFaculty, setFocusedFaculty] = useState<string | null>(null);
  const [focusedSearchIndex, setFocusedSearchIndex] = useState(-1);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  const hasLab = determineIfCourseHasLab(courseNameDisplay, allFacultyData);

  const handleReorder = (currentIndex: number, direction: 'up' | 'down') => {
    const combined = [...leftColumnFaculty, ...rightColumnFaculty];
    const itemToMove = combined[currentIndex];
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= combined.length) {
      return;
    }

    // Swap elements
    [combined[currentIndex], combined[newIndex]] = [combined[newIndex], combined[currentIndex]];

    setLeftColumnFaculty(combined.slice(0, 5));
    setRightColumnFaculty(combined.slice(5));
    setFocusedFaculty(itemToMove);
  };

  const handleFacultyKeyDown = (e: React.KeyboardEvent, index: number, column: 'left' | 'right') => {
    if (e.ctrlKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const overallIndex = column === 'left' ? index : leftColumnFaculty.length + index;
      const direction = e.key === 'ArrowUp' ? 'up' : 'down';
      handleReorder(overallIndex, direction);
    }
  };

  const showBackButton = !courseName.includes('Edit Faculty') && !courseName.includes('Add Lab') && !courseName.includes('Edit Lab');

  // Helper function to get all faculty preferences combined
  const getAllFacultyPreferences = (): string[] => {
    return [...leftColumnFaculty, ...rightColumnFaculty];
  };

  const autoAssignLabSlots = (facultyName: string) => {
    if (!hasLab) return;

    const theorySlots = theoryCourseSlots;
    const labSlots = new Set<string>();

    const labSlotMap = new Map(config.slotConflictPairs as [string, string][]);

    theorySlots.forEach(theorySlot => {
      if (labSlotMap.has(theorySlot)) {
        labSlots.add(labSlotMap.get(theorySlot)!);
      }
    });

    if (labSlots.size > 0) {
      setConfirmedLabAssignments(prev => {
        const newAssignments = new Map(prev);
        newAssignments.set(facultyName, Array.from(labSlots));
        return newAssignments;
      });
    }
  };

  const handleRemoveFaculty = (index: number, column: string) => {
    let removedFacultyName: string | undefined;

    if (column === LEFT_COLUMN) {
      const newLeft = [...leftColumnFaculty];
      removedFacultyName = newLeft.splice(index, 1)[0];
      if (rightColumnFaculty.length > 0) {
        newLeft.push(rightColumnFaculty[0]);
        setRightColumnFaculty(rightColumnFaculty.slice(1));
      }
      setLeftColumnFaculty(newLeft);
    } else {
      const newRight = [...rightColumnFaculty];
      removedFacultyName = newRight.splice(index, 1)[0];
      setRightColumnFaculty(newRight);
    }

    if (removedFacultyName) {
      setConfirmedLabAssignments(prev => {
        const newAssignments = new Map(prev || []);
        newAssignments.delete(removedFacultyName!.toLowerCase());
        return newAssignments;
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && initialFacultyPreferences && initialFacultyPreferences.length > 0) {
      const baseName = courseNameDisplay.includes(' ') ? courseNameDisplay.split(' ').slice(0, -1).join(' ') : courseNameDisplay;
      if ((!baseName.endsWith(' Lab') && courseName.includes('Add Lab')) || courseName.includes('Edit Lab')) {
        setIsLabModalOpen(true);
      }
    }
  }, [isOpen, initialFacultyPreferences, courseNameDisplay, courseName]);

  useEffect(() => {
    if (isOpen) {
      setFacultyName('');
      setConfirmedLabAssignments(initialFacultyLabAssignments);
      setInitialFacultyState(initialFacultyPreferences);
      setShowConfirm(false);
      setShowEditLab(false);
      setIsEditMode(initialFacultyPreferences.length > 0);
      
      if (initialFacultyPreferences && initialFacultyPreferences.length > 0) {
        setLeftColumnFaculty(initialFacultyPreferences.slice(0, 5));
        setRightColumnFaculty(initialFacultyPreferences.slice(5));
      } else {
        setLeftColumnFaculty([]);
        setRightColumnFaculty([]);
      }
      setValidationError(null);
      
      setShowInput(true);
    }
  }, [isOpen, initialFacultyPreferences, initialFacultyLabAssignments]);

  useEffect(() => {
    if (!isEditMode) {
        setShowConfirm(false);
        setShowEditLab(false);
        return;
    }

    const currentFaculty = getAllFacultyPreferences();
    
    if (JSON.stringify(initialFacultyState) === JSON.stringify(currentFaculty)) {
        setShowEditLab(false);
        setShowConfirm(false);
        return;
    }

    if (!isLabCourseAssociated) {
        setShowConfirm(true);
        setShowEditLab(false);
        return;
    }

    let showEdit = false;
    let showConf = false;
    
    const p1Changed = initialFacultyState.length > 0 && (currentFaculty.length === 0 || initialFacultyState[0] !== currentFaculty[0]);

    if (currentFaculty.length > initialFacultyState.length) {
        showEdit = true;
    } 
    else if (p1Changed) {
        showEdit = true;
        if (currentFaculty.length > 0) {
            const newP1 = currentFaculty[0];
            setConfirmedLabAssignments(prevAssignments => {
                if (prevAssignments && prevAssignments.has(newP1.toLowerCase())) {
                    const newAssignments = new Map(prevAssignments);
                    newAssignments.delete(newP1.toLowerCase());
                    return newAssignments;
                }
                return prevAssignments;
            });
        }
    } 
    else {
        showConf = true;
    }

    setShowEditLab(showEdit);
    setShowConfirm(showConf);

  }, [leftColumnFaculty, rightColumnFaculty, initialFacultyState, isEditMode, isLabCourseAssociated, confirmedLabAssignments]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      console.log("Initial faculty preferences:", initialFacultyPreferences);
    }
  }, [isOpen, initialFacultyPreferences]);

  useEffect(() => {
    console.log("Current faculty preferences:", getAllFacultyPreferences());
    console.log("Current facultyName:", facultyName);
  }, [leftColumnFaculty, rightColumnFaculty, facultyName]);

  useEffect(() => {
    if (focusedFaculty) {
      const element = document.querySelector(`[data-faculty-name="${focusedFaculty}"]`) as HTMLElement;
      if (element) {
        element.focus();
      }
      setFocusedFaculty(null);
    }
  }, [focusedFaculty, leftColumnFaculty, rightColumnFaculty]);

  useEffect(() => {
    if (!isFacultySearchVisible) {
      setFocusedSearchIndex(-1);
    }
  }, [isFacultySearchVisible]);

  useEffect(() => {
    if (focusedSearchIndex >= 0 && searchResultsRef.current) {
      const searchResults = searchResultsRef.current;
      const focusedElement = searchResults.children[focusedSearchIndex] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedSearchIndex]);

  const handleFacultyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setFacultyName(value);

    if (value.trim().length > 0 && !value.startsWith('/')) {
      setShowManualAddHint(true);
    } else {
      setShowManualAddHint(false);
    }

    if (value.startsWith('/')) {
      setIsFacultySearchVisible(true);
      const query = value.substring(1);
      const alreadySelected = getAllFacultyPreferences();
      
      const matchingTheoryFacultyNames = searchFaculty(
        query,
        allFacultyData,
        courseNameDisplay,
        theoryCourseSlots,
        alreadySelected
      );

      const isMorningTheory = theoryCourseSlots.some(slot => config.morningTheorySlotsConst.includes(slot));

      const results = matchingTheoryFacultyNames.flatMap(facultyName => {
        let labEntriesForFaculty: Faculty[];

        if (courseType === 'ETH') {
          const theorySlotString = theoryCourseSlots.slice().sort().join('+');
          
          const ethEntryIndex = allFacultyData.findIndex(
            (entry) =>
              entry.CODE === courseCode &&
              entry.FACULTY.toLowerCase() === facultyName.toLowerCase() &&
              entry.TYPE === 'ETH' &&
              entry.SLOT.split('+').sort().join('+') === theorySlotString
          );

          if (ethEntryIndex > -1) {
            const immediateElaEntry = allFacultyData
              .slice(ethEntryIndex + 1)
              .find(
                (entry) =>
                  entry.CODE === courseCode &&
                  entry.FACULTY.toLowerCase() === facultyName.toLowerCase() &&
                  entry.TYPE === 'ELA'
              );
            
            labEntriesForFaculty = immediateElaEntry ? [immediateElaEntry] : [];
          } else {
            labEntriesForFaculty = [];
          }
        } else {
          const labCourseName = `${courseNameDisplay.toLowerCase()} lab`;
          labEntriesForFaculty = allFacultyData.filter(
            (entry) =>
              entry.FACULTY.toLowerCase() === facultyName.toLowerCase() &&
              entry.TITLE.toLowerCase() === labCourseName &&
              entry.TYPE === 'LO'
          );
        }
        
        if (labEntriesForFaculty.length === 0) {
          return [{ name: facultyName, labSlots: [], id: facultyName, hasConflict: false }];
        }

        const resultsForFaculty = labEntriesForFaculty
          .map((entry, index) => {
            const labSlots = courseType === 'ETH'
              ? entry.SLOT.split('+').filter(Boolean)
              : entry.SLOT.split('+').filter(slot =>
                  isMorningTheory ? isEveningLab(slot) : isMorningLab(slot)
                );

            if (labSlots.length > 0) {
              const hasConflict = labSlots.some(labSlot => allCurrentlyUsedSlots.includes(labSlot));
              return {
                name: facultyName,
                labSlots: labSlots,
                id: `${facultyName}-${entry.SLOT}-${index}`,
                hasConflict
              };
            }
            return null;
          })
          .filter((result): result is { name: string; labSlots: string[]; id: string; hasConflict: boolean; } => result !== null);

        if (resultsForFaculty.length > 0) {
          return resultsForFaculty;
        } else {
          return [{ name: facultyName, labSlots: [], id: facultyName, hasConflict: false }];
        }
      });
      
      setFacultySearchResults(results);
    } else {
      setIsFacultySearchVisible(false);
    }
  };

  const handleSelectFaculty = (result: { name: string; labSlots: string[]; hasConflict: boolean }) => {
    const { name, labSlots } = result;
    const lowerCaseName = name.toLowerCase();
    const allFaculty = getAllFacultyPreferences();
    if (allFaculty.includes(lowerCaseName)) {
      setValidationError(`Faculty "${name}" has already been added.`);
      setDuplicateFaculty(lowerCaseName);
      setTimeout(() => setDuplicateFaculty(null), 1000); // Reset after 1s
      return;
    }
    if (allFaculty.length >= 10) {
      setValidationError("You can add a maximum of 10 faculty preferences.");
      return;
    }
    setValidationError(null);
    setShowManualAddHint(false);

    if (labSlots.length > 0) {
      setConfirmedLabAssignments(prev => {
        const newAssignments = new Map(prev);
        newAssignments.set(lowerCaseName, labSlots);
        return newAssignments;
      });
    } else {
      autoAssignLabSlots(lowerCaseName);
    }

    if (leftColumnFaculty.length < 5) {
      setLeftColumnFaculty([...leftColumnFaculty, lowerCaseName]);
    } else {
      setRightColumnFaculty([...rightColumnFaculty, lowerCaseName]);
    }
    setFacultyName('');
    setIsFacultySearchVisible(false);
    inputRef.current?.focus();
  };

  const handleManualAddFaculty = () => {
    const trimmedName = facultyName.trim();
    if (!trimmedName || trimmedName.startsWith('/')) return;

    const allFaculty = getAllFacultyPreferences();
    if (allFaculty.includes(trimmedName)) {
      setValidationError(`Faculty "${trimmedName}" has already been added.`);
      setDuplicateFaculty(trimmedName);
      setTimeout(() => setDuplicateFaculty(null), 1000); // Reset after 1s
      return;
    }
    if (allFaculty.length >= 10) {
      setValidationError("You can add a maximum of 10 faculty preferences.");
      return;
    }
    
    setValidationError(null);
    setShowManualAddHint(false);

    autoAssignLabSlots(trimmedName);

    if (leftColumnFaculty.length < 5) {
      setLeftColumnFaculty([...leftColumnFaculty, trimmedName]);
    } else {
      setRightColumnFaculty([...rightColumnFaculty, trimmedName]);
    }
    setFacultyName('');
    setIsFacultySearchVisible(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isFacultySearchVisible) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedSearchIndex(prev => 
          prev < facultySearchResults.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedSearchIndex(prev => prev > -1 ? prev - 1 : prev);
      } else if (e.key === 'Enter' && focusedSearchIndex !== -1) {
        e.preventDefault();
        handleSelectFaculty(facultySearchResults[focusedSearchIndex]);
        setFocusedSearchIndex(-1);
      }
    } else if ((e.key === 'Enter' || (e.ctrlKey && e.key === 'ArrowDown')) && !isFacultySearchVisible) {
      e.preventDefault();
      handleManualAddFaculty();
    }
  };

  const handleSubmit = () => {
    if (isSlashCommandSelection && hasLabComponent) {
      setIsLabModalOpen(true);
      return;
    }

    const allFaculty = getAllFacultyPreferences();
    const isLabCourse = courseName.toLowerCase().includes('lab');

    if (isLabCourse && allFaculty.length > 0 && (!confirmedLabAssignments || confirmedLabAssignments.size === 0 || Array.from(confirmedLabAssignments.values()).every(slots => slots.length === 0))) {
      setValidationError("A lab course must have at least one lab slot assigned to a faculty.");
      return;
    }

    setValidationError(null);
    onSubmit(allFaculty, isLabCourseAssociated, confirmedLabAssignments);
  };

  const handleAddLabCourse = () => {
    setIsLabModalOpen(true);
  };

  const handleLabModalClose = () => {
    setIsLabModalOpen(false);
  };

  const handleLabSlotConfirmAndProceed = (labAssignmentsFromModal: Map<string, string[]>) => {
    const allFaculty = getAllFacultyPreferences();
    onSubmit(allFaculty, true, labAssignmentsFromModal);
  };

  const handleSkip = () => {
    onSubmit([], false, undefined);
  };

  const inputContainerClass = `flex items-center mb-3 relative transition-all duration-200 ${!facultyName.trim() && !isInputFocused ? 'opacity-95' : ''}`;
  
  const inputNumberClass = `flex items-center justify-center h-9 w-9 rounded-full mr-3 transition-all duration-200 bg-blue-100 text-blue-700 font-medium shadow-sm hover:shadow-md hover:scale-105 hover:bg-blue-200 active:scale-95`;

  const inputClass = `w-full h-[50px] py-3 px-4 border border-gray-300 ring-0 rounded-lg outline-none bg-white text-[16px] leading-[24px] focus:border-gray-300`;

  const handleClearInput = () => {
    setFacultyName('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const shouldShowRightColumn = isLabModalOpen 
    ? rightColumnFaculty.length > 0
    : rightColumnFaculty.length > 0 || 
      leftColumnFaculty.length >= 5;
  
  const totalFaculty = leftColumnFaculty.length + rightColumnFaculty.length;

  const facultyInput = (
    !isLabModalOpen && totalFaculty < 10 && (
      <div className={`px-2 ${inputContainerClass}`}>
        <div className={inputNumberClass}>
          {totalFaculty + 1}
        </div>
        <div className="flex-1 flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={facultyName}
              onChange={handleFacultyNameChange}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onKeyDown={handleKeyDown}
              className={inputClass}
              placeholder="Use '/' to search or enter faculty name..."
              disabled={totalFaculty >= 10}
              maxLength={20}
            />
            {showManualAddHint && (
              <div className="absolute top-full right-0 mt-2 text-sm text-gray-500 animate-fade-in-down">
                Press <span className="font-semibold text-gray-700">Enter</span> to add
              </div>
            )}
            {isFacultySearchVisible && (
              <div ref={searchResultsRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                {facultySearchResults.length > 0 ? (
                  facultySearchResults.map((result, index) => {
                    const isFirstPreference = getAllFacultyPreferences().length === 0;
                    const isDisabled = isFirstPreference && result.hasConflict;
                    const isFocused = index === focusedSearchIndex;

                    return (
                      <button
                        key={result.id}
                        onClick={() => handleSelectFaculty(result)}
                        disabled={isDisabled}
                        className={`w-full text-left hover:bg-gray-100 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''} ${isFocused ? 'bg-gray-100' : ''}`}
                      >
                        <div className="px-4 py-2 border-b border-gray-100">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-gray-900">{result.name.toLowerCase()}</span>
                            {result.hasConflict && isFirstPreference && (
                              <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-md">Conflict</span>
                            )}
                          </div>
                          {result.labSlots.length > 0 && (
                            <div className="flex gap-1.5">
                              <span className="bg-gray-700 text-white px-2 py-0.5 text-xs font-mono rounded">
                                {result.labSlots.join('+')}
                              </span>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="px-4 py-2 text-gray-500">No results found</div>
                )}
              </div>
            )}
          </div>
          <div className="w-8 h-8 flex-shrink-0">
            {facultyName.trim() && (
              <button
                onClick={handleClearInput}
                className="w-full h-full flex items-center justify-center text-gray-400 transition-colors rounded-full"
              >
                <IoClose size={24} />
              </button>
            )}
          </div>
        </div>
      </div>
    )
  );

  if (!isOpen) return null;

  const isActionDisabled = getAllFacultyPreferences().length === 0;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 min-h-screen">
      <div className={`fixed inset-0 bg-gray-900 bg-opacity-40 backdrop-blur-sm ${isLabModalOpen ? 'opacity-0' : ''}`}></div>
      <div className={`bg-white rounded-2xl shadow-xl z-10 relative transition-all duration-300 transform -translate-y-4 p-8 pb-6 ${
        shouldShowRightColumn 
          ? 'w-[900px]' 
          : 'w-[550px]'
      }`}>
        <div className="flex justify-between items-center -mt-2 mb-6">
          {showBackButton ? (
            <button 
              onClick={() => onClose(undefined, undefined)}
              className="text-gray-600 hover:text-black transition-colors flex items-center"
              aria-label="Back"
            >
              <IoArrowBack size={24} />
            </button>
          ) : (
            <div className="w-6 h-6" />
          )}
          <button 
            onClick={onForceClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close modal"
          >
            <IoClose size={24} />
          </button>
        </div>

        <h2 className="text-2xl font-semibold text-gray-900 mb-2 text-center break-words">{`Faculty Preference for "${courseNameDisplay}"`}</h2>
        
        <p className="text-center mb-8 text-gray-600 flex items-center justify-center gap-3">
          Selected slot: <span className={`inline-block px-3 py-1 text-sm font-medium rounded-md ${PALETTES[palette].colors[colorIndex]}`}>
            {slotDisplay}
          </span>
          <span className="flex items-center space-x-1 bg-gray-100 rounded-lg px-2 py-1">
            <span className="text-xs text-gray-500">Credits:</span>
            <span className="text-sm font-medium text-gray-900">{courseCredits}</span>
          </span>
        </p>

        <div className={`flex mb-8 gap-x-6 ${!shouldShowRightColumn ? 'justify-center' : ''}`}>
            <div className={shouldShowRightColumn ? 'flex-1' : 'w-full max-w-[450px]'}>
              <div className="px-2">
                {leftColumnFaculty.map((faculty, index) => (
                  <div
                    key={faculty}
                    data-faculty-name={faculty}
                    onKeyDown={(e) => handleFacultyKeyDown(e, index, 'left')}
                    tabIndex={0}
                    className={`flex items-center mb-5 relative transition-all duration-200 rounded-lg -m-1 p-1 focus:outline-none focus:ring-2 focus:ring-gray-300 ${duplicateFaculty === faculty ? 'animate-shake' : ''}`}
                  >
                    <div className="flex items-center justify-center h-9 w-9 rounded-full bg-blue-100 text-blue-700 font-medium mr-3 shadow-sm hover:shadow-md hover:scale-105 hover:bg-blue-200 transition-all duration-200 active:scale-95">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="relative py-3 px-4 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors group break-words text-[16px] leading-[24px]">
                        {faculty}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFaculty(index, LEFT_COLUMN)}
                      className="ml-3 text-red-500 hover:text-red-700 transition-transform hover:scale-110"
                    >
                      <IoClose size={24} />
                    </button>
                  </div>
                ))}
              </div>

              {!isLabModalOpen && leftColumnFaculty.length < 5 && totalFaculty < 10 && (
                <div className={`px-2 ${inputContainerClass}`}>
                  <div className={inputNumberClass}>
                    {totalFaculty + 1}
                  </div>
                  <div className="flex-1 flex items-center gap-2">
                    <div className="flex-1 relative">
                      <input
                        ref={inputRef}
                        type="text"
                        value={facultyName}
                        onChange={handleFacultyNameChange}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                        onKeyDown={handleKeyDown}
                        className={inputClass}
                        placeholder="Enter Faculty name or use '/' to search.."
                        disabled={totalFaculty >= 10}
                        maxLength={20}
                      />
                      {showManualAddHint && (
                        <div className="absolute top-full right-0 mt-2 text-sm text-gray-500 animate-fade-in-down">
                          Press <span className="font-semibold text-gray-700">Enter</span> to add
                        </div>
                      )}
                      {isFacultySearchVisible && (
                        <div ref={searchResultsRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto">
                          {facultySearchResults.length > 0 ? (
                            facultySearchResults.map((result, index) => {
                              const isFirstPreference = getAllFacultyPreferences().length === 0;
                              const isDisabled = isFirstPreference && result.hasConflict;
                              const isFocused = index === focusedSearchIndex;

                              return (
                                <button
                                  key={result.id}
                                  onClick={() => handleSelectFaculty(result)}
                                  disabled={isDisabled}
                                  className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${isDisabled ? 'cursor-not-allowed opacity-50' : ''} ${isFocused ? 'bg-gray-50' : ''}`}
                                >
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-gray-900 font-medium">{result.name.toLowerCase()}</span>
                                    {result.hasConflict && isFirstPreference && (
                                      <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-md">Conflict</span>
                                    )}
                                  </div>
                                  {result.labSlots.length > 0 && (
                                    <div className="flex gap-1.5">
                                      <span className="bg-gray-700 text-white px-2 py-0.5 text-xs font-mono rounded">
                                        {result.labSlots.join('+')}
                                      </span>
                                    </div>
                                  )}
                                </button>
                              );
                            })
                          ) : (
                            <div className="px-4 py-3 text-gray-500">No results found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {shouldShowRightColumn && (
              <div className="flex-1">
                <div className="px-2">
                  {rightColumnFaculty.map((faculty, index) => (
                    <div
                      key={faculty}
                      data-faculty-name={faculty}
                      onKeyDown={(e) => handleFacultyKeyDown(e, index, 'right')}
                      tabIndex={0}
                      className={`flex items-center mb-5 relative transition-all duration-200 rounded-lg -m-1 p-1 focus:outline-none focus:ring-2 focus:ring-gray-300 ${duplicateFaculty === faculty ? 'animate-shake' : ''}`}
                    >
                      <div className="flex items-center justify-center h-9 w-9 rounded-full bg-blue-100 text-blue-700 font-medium mr-3 shadow-sm hover:shadow-md hover:scale-105 hover:bg-blue-200 transition-all duration-200 active:scale-95">
                        {leftColumnFaculty.length + index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="relative py-3 px-4 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors group break-words text-[16px] leading-[24px]">
                          {faculty}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFaculty(index, RIGHT_COLUMN)}
                        className="ml-3 text-red-500 hover:text-red-700 transition-transform hover:scale-110"
                      >
                        <IoClose size={24} />
                      </button>
                    </div>
                  ))}
                </div>

                {!isLabModalOpen && leftColumnFaculty.length >= 5 && rightColumnFaculty.length < 5 && totalFaculty < 10 && facultyInput}
              </div>
            )}
          </div>

        {validationError && (
          <p className="text-sm text-red-500 mb-5 text-center px-2 italic">
            {validationError}
          </p>
        )}

        <div className="flex flex-col items-center">
          {/* Keyboard shortcut instruction */}
          <div className="hidden sm:flex items-center justify-center gap-2 mb-4 text-sm text-gray-700">
            <span>Use <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs font-mono">Ctrl + ↑/↓</kbd> to reorder faculty preferences</span>
          </div>

          <div className={`flex justify-end w-full space-x-3`}>
            {isEditMode ? (
              <>
                {showConfirm && (
                  <button
                    onClick={handleSubmit}
                    className="bg-black text-white font-medium text-sm py-3 px-6 rounded-lg transition-colors"
                  >
                    Confirm
                  </button>
                )}
                {showEditLab && (
                  <button
                    onClick={handleAddLabCourse}
                    className="bg-black text-white font-medium text-sm py-3 px-6 rounded-lg transition-colors"
                  >
                    {`Edit "${courseNameDisplay} Lab"`}
                  </button>
                )}
              </>
            ) : (
              <div className="flex justify-end w-full space-x-3">
                {!(isSlashCommandSelection && hasLabComponent) && hasLabComponent && (
                  <button
                    onClick={handleAddLabCourse}
                    disabled={isActionDisabled}
                    className={`px-6 py-3 rounded-lg text-sm font-medium text-white transition-colors ${
                      isActionDisabled
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                  >
                    {`Add "${courseNameDisplay} Lab"`}
                  </button>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={isActionDisabled}
                  className={`confirm-btn px-6 py-3 rounded-lg text-sm font-medium text-white transition-colors ${
                    isActionDisabled
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-black'
                  }`}
                >
                  Confirm
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {isLabModalOpen && (
        <LabSlotModal
          isOpen={isLabModalOpen}
          onClose={handleLabModalClose}
          onForceClose={onForceClose}
          onSubmit={handleLabSlotConfirmAndProceed}
          courseName={courseNameDisplay}
          theorySlot={slotDisplay || ''}
          theoryCourseActualSlots={theoryCourseSlots}
          facultyPreferences={getAllFacultyPreferences()}
          allCurrentlyUsedSlots={allCurrentlyUsedSlots}
          slotConflictPairs={slotConflictPairs}
          slotColor={PALETTES[palette as keyof typeof PALETTES].colors[colorIndex]}
          initialAssignments={confirmedLabAssignments}
          courseType={courseType}
          courseCode={courseCode}
          allFacultyData={allFacultyData}
        />
      )}
    </div>
  );
};

export default FacultyPreferenceModal;